import { Box, CircularProgress, Typography, alpha } from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { SideDrawer } from "@/components/common/SideDrawer";
import { useAppTheme } from "@/hooks/useAppTheme";
import { PdfViewerWithHighlights, type PdfHighlight } from "@/components/common/PdfViewer";

// =============================================================================
// Types
// =============================================================================

interface PdfSourcePreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  pdfData: Uint8Array | null;
  isLoading: boolean;
  error: string | null;
  /** Page to scroll into view (1-indexed) */
  focusPage: number | null;
  /** Total pages in the PDF */
  pageCount: number | null;
  /** Field-level bounding box highlights to render on PDF pages */
  highlights?: PdfHighlight[];
}

// =============================================================================
// Component
// =============================================================================

export const PdfSourcePreviewDrawer = ({
  open,
  onClose,
  pdfData,
  isLoading,
  error,
  focusPage,
  pageCount,
  highlights = [],
}: PdfSourcePreviewDrawerProps) => {
  const { theme } = useAppTheme();
  const title = pageCount ? `Source: PDF (${pageCount} pages)` : "Source: PDF";

  return (
    <SideDrawer title={title} width={650} open={open} onClose={onClose} hideHandle>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 1.5 }}>
        {/* Info banner */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: 1,
            borderColor: alpha(theme.palette.info.main, 0.2),
          }}
        >
          <InfoIcon sx={{ fontSize: 16, color: "info.main", mt: 0.25 }} />
          <Typography variant="caption" color="text.secondary">
            {highlights.length > 0
              ? "Field-level highlights shown for the selected record."
              : "Page-level navigation available. Select a record to see field highlights."}
          </Typography>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="error.main">
              {error}
            </Typography>
          </Box>
        )}

        {!isLoading && !error && pdfData && (
          <Box sx={{ overflow: "auto", flex: 1 }}>
            <PdfViewerWithHighlights source={pdfData} focusPage={focusPage} highlights={highlights} />
          </Box>
        )}
      </Box>
    </SideDrawer>
  );
};
